import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { Specialty } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/fetchSpecialties/types";
import {
  createSpecialtyAction,
  deleteSpecialtyAction,
  fetchSpecialtiesAction,
  updateSpecialtyAction,
} from "@/modules/administration/features/EmployeeDirectoryManagement/slices/specialtySlice";
import { useNotification } from "@/context/NotificationContext";

export const useSpecialtyManagementPage = () => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const list = useAppSelector(
    (state: RootState) => state.employeeDirectorySpecialty.list,
  );
  const loading = useAppSelector(
    (state: RootState) => state.employeeDirectorySpecialty.loadingList,
  );
  const error = useAppSelector(
    (state: RootState) => state.employeeDirectorySpecialty.listError,
  );
  const loadingCreate = useAppSelector(
    (state: RootState) => state.employeeDirectorySpecialty.loadingCreate,
  );
  const loadingUpdate = useAppSelector(
    (state: RootState) => state.employeeDirectorySpecialty.loadingUpdate,
  );
  const loadingDelete = useAppSelector(
    (state: RootState) => state.employeeDirectorySpecialty.loadingDelete,
  );

  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editTarget, setEditTarget] = useState<Specialty | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Specialty | null>(null);

  const refetch = useCallback(() => {
    dispatch(fetchSpecialtiesAction());
  }, [dispatch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const openCreateDrawer = useCallback(() => setShowCreateDrawer(true), []);
  const closeCreateDrawer = useCallback(() => setShowCreateDrawer(false), []);

  const openEditDrawer = useCallback((specialty: Specialty) => {
    setEditTarget(specialty);
  }, []);
  const closeEditDrawer = useCallback(() => setEditTarget(null), []);

  const openDeleteModal = useCallback((specialty: Specialty) => {
    setDeleteTarget(specialty);
  }, []);
  const closeDeleteModal = useCallback(() => setDeleteTarget(null), []);

  const handleCreate = useCallback(
    (values: { name: string }) => {
      dispatch(
        createSpecialtyAction(values, () => {
          showNotification?.(
            "Success",
            "Specialty created successfully.",
            "success",
          );
          closeCreateDrawer();
          refetch();
        }),
      );
    },
    [dispatch, closeCreateDrawer, refetch, showNotification],
  );

  const handleUpdate = useCallback(
    (values: { name: string }) => {
      if (!editTarget) return;
      dispatch(
        updateSpecialtyAction(editTarget.uuid, values, () => {
          showNotification?.(
            "Success",
            "Specialty updated successfully.",
            "success",
          );
          closeEditDrawer();
          refetch();
        }),
      );
    },
    [dispatch, editTarget, closeEditDrawer, refetch, showNotification],
  );

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    dispatch(
      deleteSpecialtyAction(deleteTarget.uuid, () => {
        showNotification?.(
          "Success",
          "Specialty deleted successfully.",
          "success",
        );
        closeDeleteModal();
        refetch();
      }),
    );
  }, [dispatch, deleteTarget, closeDeleteModal, refetch, showNotification]);

  return {
    list,
    loading,
    error,
    loadingCreate,
    loadingUpdate,
    loadingDelete,
    showCreateDrawer,
    editTarget,
    deleteTarget,
    openCreateDrawer,
    closeCreateDrawer,
    openEditDrawer,
    closeEditDrawer,
    openDeleteModal,
    closeDeleteModal,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
};
